import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, notifyError } from "@/lib/api";
import Dropzone from "@/components/Dropzone";
import { Plus, Camera, MagicWand, Trash, X, Receipt as ReceiptIcon, Copy, LinkSimple } from "@phosphor-icons/react";

const CATS = ["General", "Equipment", "Marketing", "Training", "Travel", "Meals", "Personnel", "Software"];

const StatusBadge = ({ s }) => (
  <span className={`badge-${s} px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold`}>{s}</span>
);

export default function Receipts() {
  const [receipts, setReceipts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ vendor: "", amount: "", date: new Date().toISOString().slice(0,10), category: "General", vat_amount: "", contains_alcohol: false, is_bik: false, notes: "" });
  const [imgB64, setImgB64] = useState(null);
  const [ocring, setOcring] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dupes, setDupes] = useState(null);
  const [dupeSel, setDupeSel] = useState({});

  const load = useCallback(async () => {
    const r = await api.get("/receipts"); setReceipts(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const findDupes = async () => {
    try { const r = await api.get("/receipts/duplicates"); setDupes(r.data.groups || []); setDupeSel({}); }
    catch (e) { notifyError(e); }
  };

  const deleteSelectedDupes = async () => {
    const ids = Object.keys(dupeSel).filter((k) => dupeSel[k]);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} duplicate receipt${ids.length === 1 ? "" : "s"}? This can't be undone.`)) return;
    try {
      for (const id of ids) await api.delete(`/receipts/${id}`);
      setDupes(null); load();
    } catch (e) { notifyError(e); }
  };

  const fileToB64 = (file) => new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => res(reader.result.split(",")[1]);
    reader.onerror = rej;
    reader.readAsDataURL(file);
  });

  const handleImage = async (file) => {
    if (!file) return;
    const b64 = await fileToB64(file);
    setImgB64(b64);
  };

  const handleDropAndOCR = async (file) => {
    if (!file) return;
    const b64 = await fileToB64(file);
    setImgB64(b64);
    setOpen(true);
    // Kick off OCR immediately
    setOcring(true);
    try {
      const r = await api.post("/receipts/ocr", { image_base64: b64 });
      const d = r.data.data || {};
      setForm(f => ({
        ...f,
        vendor: d.vendor || f.vendor,
        amount: d.amount ?? f.amount,
        date: d.date || f.date,
        category: d.category || f.category,
        vat_amount: d.vat_amount ?? f.vat_amount,
        contains_alcohol: Boolean(d.contains_alcohol),
      }));
    } catch (e) { alert("OCR failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setOcring(false); }
  };

  const runOCR = async () => {
    if (!imgB64) return;
    setOcring(true);
    try {
      const r = await api.post("/receipts/ocr", { image_base64: imgB64 });
      const d = r.data.data || {};
      setForm(f => ({
        ...f,
        vendor: d.vendor || f.vendor,
        amount: d.amount ?? f.amount,
        date: d.date || f.date,
        category: d.category || f.category,
        vat_amount: d.vat_amount ?? f.vat_amount,
        contains_alcohol: Boolean(d.contains_alcohol),
      }));
    } catch (e) { alert("OCR failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setOcring(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const amt = Number(form.amount || 0);
      try {
        const chk = await api.get("/receipts/check-duplicate", { params: { vendor: form.vendor, amount: amt, date: form.date } });
        if (chk.data?.duplicate && !window.confirm(`This looks like a receipt you already added — ${chk.data.match.vendor} €${Number(chk.data.match.amount).toFixed(2)} on ${chk.data.match.date}. Add it anyway?`)) {
          setSaving(false); return;
        }
      } catch { /* non-blocking check */ }
      await api.post("/receipts", {
        ...form,
        amount: amt,
        vat_amount: Number(form.vat_amount || 0),
        image_base64: imgB64,
      });
      setOpen(false);
      setForm({ vendor: "", amount: "", date: new Date().toISOString().slice(0,10), category: "General", vat_amount: "", contains_alcohol: false, is_bik: false, notes: "" });
      setImgB64(null);
      load();
    } catch (e) { notifyError(e); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this receipt?")) return;
    await api.delete(`/receipts/${id}`); load();
  };

  const viewFile = async (id) => {
    try {
      const r = await api.get(`/receipts/${id}/file`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank", "noopener");
    } catch (e) { notifyError(e, "No original file stored for this receipt"); }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-2">Receipts</div>
          <h1 className="font-display text-4xl">Capture & compliance-check</h1>
          <p className="text-white/60 text-sm mt-2">Take a photo or drop any receipt, invoice or PDF — AI reads it, extracts the fields and flags Alcohol / BIK / VAT anomalies against Revenue.ie rules.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="btn-find-dupes-receipts" onClick={findDupes} disabled={receipts.length === 0} className="btn-ghost"><Copy weight="fill" size={16} /> Find duplicates</button>
          <button data-testid="btn-new-receipt" onClick={() => setOpen(true)} className="btn-ghost"><Plus weight="bold" size={16} /> Manual entry</button>
        </div>
      </div>
      {/* Dropzone */}
      <Dropzone
        testId="dropzone-receipt"
        accept="image/*,application/pdf,.pdf,.heic,.heif"
        icon={ReceiptIcon}
        label="Drop receipt, invoice or PDF here"
        sublabel="JPG · PNG · HEIC · PDF (multi-page) — AI auto-fills vendor, amount, VAT, date and flags Revenue.ie compliance issues"
        onFile={handleDropAndOCR}
      />

      <div className="flex gap-3 flex-wrap">
        <label className="btn-ghost cursor-pointer" data-testid="btn-camera-receipt">
          <Camera size={16} weight="fill" /> Take photo
          <input type="file" accept="image/*" capture="environment" hidden
                 onChange={(e) => handleDropAndOCR(e.target.files?.[0])} />
        </label>
        <button data-testid="btn-new-receipt-inline" onClick={() => setOpen(true)} className="btn-ghost">
          <Plus weight="bold" size={16} /> Manual entry
        </button>
      </div>

      <div className="glass rounded-xl overflow-hidden">
        {receipts.length === 0 ? (
          <div className="p-12 text-center text-white/50 text-sm">
            No receipts yet. Drop an image above or click <strong className="text-white/80">Manual entry</strong>.
          </div>
        ) : (
          <table className="data">
            <thead><tr><th>Vendor</th><th>Category</th><th>Date</th><th className="text-right">Amount</th><th className="text-right">VAT</th><th>Flags</th><th></th></tr></thead>
            <tbody>
              {receipts.map(r => (
                <tr key={r.receipt_id} data-testid={`row-receipt-${r.receipt_id}`}>
                  <td className="font-medium">{r.vendor}</td>
                  <td className="text-white/60">{r.category}</td>
                  <td className="text-white/60 mono text-xs">{r.date}</td>
                  <td className="num">€{Number(r.amount).toFixed(2)}</td>
                  <td className="num text-white/60">€{Number(r.vat_amount||0).toFixed(2)}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <StatusBadge s={r.status} />
                      {r.flags?.length > 0 && <span className="text-white/40 text-xs">{r.flags.length}</span>}
                    </div>
                  </td>
                  <td className="text-right whitespace-nowrap">
                    {(r.has_image || r.storage_path) && (
                      <button data-testid={`btn-view-file-${r.receipt_id}`} onClick={() => viewFile(r.receipt_id)} title="View original file (audit)" className="text-white/40 hover:text-[#FFB020] mr-2"><ReceiptIcon size={16} /></button>
                    )}
                    {r.matched_txn_id && <span title="Matched to a bank transaction" className="inline-flex mr-2 text-[#00A86B] align-middle"><LinkSimple size={15} /></span>}
                    <button data-testid={`btn-del-${r.receipt_id}`} onClick={() => del(r.receipt_id)} className="text-white/40 hover:text-[#E63946]"><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto">
            <motion.div initial={{y:20,opacity:0}} animate={{y:0,opacity:1}} exit={{y:20,opacity:0}}
              className="glass rounded-2xl w-full max-w-2xl my-8 p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="eyebrow mb-1">{ocring ? "AI is reading your receipt…" : "New receipt"}</div>
                  <div className="font-display text-2xl">Review & save</div>
                </div>
                <button onClick={() => { setOpen(false); setImgB64(null); }} data-testid="btn-close-modal" className="text-white/60 hover:text-white"><X size={20}/></button>
              </div>

              <form onSubmit={submit} className="space-y-5">
                <div className="rounded-xl border border-dashed border-white/15 bg-black/20 p-4 flex flex-col sm:flex-row items-center gap-4">
                  <label className="cursor-pointer btn-ghost text-sm">
                    <Camera size={16} /> {imgB64 ? "Replace image" : "Upload image"}
                    <input type="file" accept="image/*" hidden onChange={(e) => handleImage(e.target.files?.[0])} data-testid="input-receipt-image"/>
                  </label>
                  {imgB64 && (
                    <>
                      <img alt="preview" src={`data:image/jpeg;base64,${imgB64}`} className="h-16 w-16 object-cover rounded-lg border border-white/10" />
                      <button type="button" data-testid="btn-run-ocr" onClick={runOCR} disabled={ocring} className="btn-gold text-sm">
                        <MagicWand size={16} weight="fill" /> {ocring ? "Reading…" : "Auto-fill with AI"}
                      </button>
                    </>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label>Vendor</label><input data-testid="input-vendor" required value={form.vendor} onChange={e => setForm({...form, vendor: e.target.value})} placeholder="e.g. Chadwicks Ltd"/></div>
                  <div><label>Amount (€)</label><input data-testid="input-amount" required type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})}/></div>
                  <div><label>Date</label><input data-testid="input-date" type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})}/></div>
                  <div><label>Category</label>
                    <select data-testid="select-category" value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                      {CATS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div><label>VAT (€)</label><input data-testid="input-vat" type="number" step="0.01" value={form.vat_amount} onChange={e => setForm({...form, vat_amount: e.target.value})}/></div>
                  <div className="flex gap-4 items-end pb-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.contains_alcohol} onChange={e => setForm({...form, contains_alcohol: e.target.checked})} data-testid="check-alcohol" className="w-auto"/>
                      <span className="text-sm text-white/80">Alcohol</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.is_bik} onChange={e => setForm({...form, is_bik: e.target.checked})} data-testid="check-bik" className="w-auto"/>
                      <span className="text-sm text-white/80">BIK</span>
                    </label>
                  </div>
                </div>
                <div><label>Notes</label><textarea rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} data-testid="input-notes"/></div>
                <div className="flex justify-end gap-3 pt-4 border-t border-white/5">
                  <button type="button" onClick={() => { setOpen(false); setImgB64(null); }} className="btn-ghost">Cancel</button>
                  <button type="submit" disabled={saving} data-testid="btn-save-receipt" className="btn-gold">
                    {saving ? "Saving…" : "Save receipt"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {dupes && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="receipt-dupes-modal">
            <motion.div initial={{y:20,opacity:0}} animate={{y:0,opacity:1}} exit={{y:20,opacity:0}} className="glass rounded-2xl w-full max-w-2xl my-8 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="eyebrow mb-1">Duplicate receipts</div>
                  <div className="font-display text-xl">{dupes.length === 0 ? "None found" : `${dupes.length} group${dupes.length===1?"":"s"} of duplicates`}</div>
                </div>
                <button onClick={() => setDupes(null)} data-testid="btn-close-receipt-dupes" className="text-white/60 hover:text-white"><X size={20}/></button>
              </div>
              {dupes.length === 0 ? (
                <div className="text-white/50 text-sm py-8 text-center">No duplicate receipts detected. 🎉</div>
              ) : (
                <>
                  <p className="text-white/55 text-xs mb-3">We keep the first of each group by default. Tick the ones you want to remove, then confirm.</p>
                  <div className="space-y-4 max-h-96 overflow-auto">
                    {dupes.map((g, gi) => (
                      <div key={`${g.vendor}-${g.amount}-${g.date}-${gi}`} className="rounded-lg border border-white/10 p-3">
                        <div className="text-xs text-white/50 mb-2">{g.vendor || "—"} · €{Number(g.amount).toFixed(2)} · {g.date}</div>
                        {g.items.map((it, idx) => (
                          <label key={it.receipt_id} className="flex items-center gap-3 py-1.5 cursor-pointer">
                            <input type="checkbox" data-testid={`receipt-dupe-check-${it.receipt_id}`} className="w-auto"
                              checked={!!dupeSel[it.receipt_id]} disabled={idx === 0}
                              onChange={(e) => setDupeSel((s) => ({ ...s, [it.receipt_id]: e.target.checked }))} />
                            <span className="text-sm flex-1 truncate">{it.category}</span>
                            {idx === 0 && <span className="text-[10px] text-[#00A86B] uppercase tracking-wider">keep</span>}
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-white/5 mt-4">
                    <button onClick={() => setDupes(null)} className="btn-ghost">Cancel</button>
                    <button data-testid="btn-delete-receipt-dupes" onClick={deleteSelectedDupes} className="btn-gold">
                      <Trash size={15} weight="fill" /> Delete selected
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
